import * as THREE from 'three';
import { CAMERA_STATES, lerpCameraState } from './camera';

/**
 * 动画阶段定义
 */
export type AnimationStage = 'scattered' | 'assembling' | 'growing' | 'complete';

export interface AnimationConfig {
  stageRange: {
    assembling: { start: number; end: number };
    growing: { start: number; end: number };
  };
}

/**
 * 室内故事动画控制器
 */
export class IndoorStoryAnimation {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private blueprintGroup: THREE.Group;
  private buildingGroup: THREE.Group;
  private sdkOverlayGroup: THREE.Group;
  private config: AnimationConfig;
  private progress: number = 0;
  private stage: AnimationStage = 'scattered';

  constructor(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    blueprintGroup: THREE.Group,
    buildingGroup: THREE.Group,
    config?: Partial<AnimationConfig>
  ) {
    this.scene = scene;
    this.camera = camera;
    this.blueprintGroup = blueprintGroup;
    this.buildingGroup = buildingGroup;

    this.sdkOverlayGroup = this.createSdkOverlay();
    this.sdkOverlayGroup.visible = false;
    this.scene.add(this.sdkOverlayGroup);

    this.config = {
      stageRange: {
        assembling: { start: 0, end: 0.7 },
        growing: { start: 0.7, end: 1 }
      },
      ...config
    };

    this.init();
  }

  private init(): void {
    this.setInitialState();
  }

  private setInitialState(): void {
    this.buildingGroup.visible = false;

    this.camera.position.copy(CAMERA_STATES.topView.position);
    this.camera.lookAt(CAMERA_STATES.topView.target);

    this.blueprintGroup.children.forEach(group => {
      if (group instanceof THREE.Group) {
        group.children.forEach((lineObj) => {
          const line = lineObj as THREE.Line;

          const material = line.material as THREE.LineBasicMaterial;
          material.transparent = true;
          material.opacity = 0.6 + Math.random() * 0.3;

          const geom = line.geometry as THREE.BufferGeometry;
          const posAttr = geom.getAttribute('position');
          geom.setDrawRange(0, 0);
          void posAttr;

          if (line.userData.originalPositions && line.userData.scatteredPositions) {
            const posAttr2 = line.geometry.getAttribute('position');
            const scattered = line.userData.scatteredPositions;

            for (let i = 0; i < Math.min(posAttr2.count, scattered.length); i++) {
              posAttr2.setXYZ(i, scattered[i].x, scattered[i].y, scattered[i].z);
            }
            posAttr2.needsUpdate = true;

            const rotOffset = line.userData.scatterOffset?.rotY || 0;
            line.rotation.y = rotOffset + (Math.random() - 0.5) * 0.5;

            line.scale.setScalar(0.6 + Math.random() * 0.5);
          }
        });
      }
    });

    this.stage = 'scattered';
  }

  public updateProgress(progress: number): void {
    this.progress = Math.max(0, Math.min(1, progress));

    const isHero = this.progress < 0.05;

    if (isHero) {
      this.blueprintGroup.visible = false;
      this.buildingGroup.visible = false;
      this.sdkOverlayGroup.visible = false;

      this.stage = 'scattered';
      this.updateCameraHero(this.progress / 0.05);
      this.updateLightHero();
      return;
    }

    const isBlueprintUpload = this.progress >= 0.05 && this.progress < 0.18;
    if (isBlueprintUpload) {
      this.stage = 'assembling';
      this.blueprintGroup.visible = true;
      this.buildingGroup.visible = false;
      this.sdkOverlayGroup.visible = false;

      const t = (this.progress - 0.05) / 0.13;
      this.updateBlueprintUpload(t);
      this.updateCameraBlueprintUpload(t);
      this.updateLightHeroToBlueprint(1);
      return;
    }

    const isWallGeneration = this.progress >= 0.18 && this.progress < 0.60;
    if (isWallGeneration) {
      this.blueprintGroup.visible = true;

      const sectionT = (this.progress - 0.18) / 0.46;
      const assembleHold = Math.max(0, Math.min(1, sectionT * 0.9));
      const assembleProgress = 0.55 + assembleHold * 0.25;

      const growLocal = Math.max(0, Math.min(1, (sectionT - 0.18) / 0.82));
      const growProgress = this.easeInOutCubic(growLocal);

      this.stage = growProgress > 0 ? 'growing' : 'assembling';

      this.updateBlueprintAnimation(assembleProgress);
      this.updateBuildingAnimation(growProgress);
      this.updateCameraWallGeneration(sectionT);
      this.updateLightAnimation(assembleProgress, growProgress);
      return;
    }

    const isFurnitureFocus = this.progress >= 0.60 && this.progress < 0.82;
    if (isFurnitureFocus) {
      this.blueprintGroup.visible = false;
      this.buildingGroup.visible = true;
      this.sdkOverlayGroup.visible = false;

      const sectionT = (this.progress - 0.60) / 0.22;

      const growProgress = 1;
      void growProgress;

      const furnitureReveal = Math.max(0, Math.min(1, (sectionT - 0.35) / 0.65));
      if (furnitureReveal <= 0.001) {
        this.hideAllFurniture();
      } else {
        this.updateBuildingFurnitureOnly(furnitureReveal);
      }

      this.stage = 'complete';
      this.updateCameraFurnitureFocus(sectionT);
      this.updateLightAnimation(1, 1);
      return;
    }

    const isSdk = this.progress >= 0.82 && this.progress < 0.96;
    if (isSdk) {
      this.blueprintGroup.visible = false;
      this.buildingGroup.visible = true;
      this.sdkOverlayGroup.visible = true;

      const sectionT = (this.progress - 0.82) / 0.14;

      this.fadeFurnitureTo(1 - this.easeInOutCubic(Math.min(1, sectionT / 0.22)));

      const isHold = sectionT >= 0.9;
      const overlayT = sectionT <= 0.9 ? sectionT / 0.9 : 1;
      this.updateSdkOverlay(overlayT);

      if (!isHold) {
        this.updateCameraSdk(sectionT);
      }
      this.updateLightAnimation(1, 1);
      return;
    }

    const isPricing = this.progress >= 0.96;
    if (isPricing) {
      this.blueprintGroup.visible = false;
      this.buildingGroup.visible = true;
      this.sdkOverlayGroup.visible = false;

      this.showAllFurniture();
      this.updateCameraPricing((this.progress - 0.96) / 0.04);
      this.updateLightAnimation(1, 1);
      return;
    }

    if (this.progress < this.config.stageRange.assembling.end) {
      this.stage = 'assembling';
    } else if (this.progress < this.config.stageRange.growing.end) {
      this.stage = 'growing';
    } else {
      this.stage = 'complete';
    }

    const assembleProgress = this.getStageProgress('assembling');
    const growProgress = this.getStageProgress('growing');

    this.blueprintGroup.visible = true;

    this.updateBlueprintAnimation(assembleProgress);
    this.updateBuildingAnimation(growProgress);
    this.updateCameraAnimation(assembleProgress, growProgress);
    this.updateLightAnimation(assembleProgress, growProgress);
  }

  private updateCameraHero(t01: number): void {
    const t = this.easeInOutCubic(Math.max(0, Math.min(1, t01)));
    lerpCameraState(this.camera, CAMERA_STATES.topView, CAMERA_STATES.topView, t);
  }

  private updateLightHero(): void {
    const lights = this.scene.userData.lights;
    if (!lights) return;
    lights.ambient.intensity = 0.12;
    lights.main.intensity = 0.35;
    lights.top.intensity = 0.25;
  }

  private updateCameraHeroToBlueprint(t01: number): void {
    const t = this.easeInOutCubic(Math.max(0, Math.min(1, t01)));
    lerpCameraState(this.camera, CAMERA_STATES.topView, CAMERA_STATES.topView, t);
  }

  private updateLightHeroToBlueprint(t01: number): void {
    const lights = this.scene.userData.lights;
    if (!lights) return;
    lights.ambient.intensity = 0.12 + t01 * 0.03;
    lights.main.intensity = 0.35 + t01 * 0.05;
  }

  private updateBlueprintUpload(t01: number): void {
    const t = this.easeInOutCubic(Math.max(0, Math.min(1, t01)));

    const assemblePart = Math.min(1, t / 0.55);
    this.updateBlueprintAnimation(assemblePart * 0.55);

    this.blueprintGroup.children.forEach((group) => {
      if (!(group instanceof THREE.Group)) return;

      group.children.forEach((lineObj) => {
        const line = lineObj as THREE.Line;
        const geom = line.geometry as THREE.BufferGeometry;
        const posAttr = geom.getAttribute('position');
        const total = posAttr.count;

        const userType = (line.userData?.type as string | undefined) ?? '';
        const layerIndex = userType === 'wall' ? 0 : userType === 'door' || userType === 'window' ? 1 : 2;

        const start = layerIndex * 0.12;
        const end = start + 0.6;
        const local = Math.max(0, Math.min(1, (t - start) / (end - start)));
        const drawT = this.easeOutCubic(local);

        const drawCount = Math.max(2, Math.floor(total * drawT));
        geom.setDrawRange(0, Math.min(drawCount, total));

        const mat = line.material as THREE.LineBasicMaterial;
        mat.transparent = true;
        const base = userType === 'wall' ? 0.85 : userType === 'door' ? 0.8 : userType === 'window' ? 0.65 : 0.55;
        mat.opacity = base;

        if (drawT < 1) {
          mat.opacity = Math.min(1, mat.opacity + (1 - drawT) * 0.03);
        }
      });
    });
  }

  private updateCameraBlueprintUpload(t01: number): void {
    const t = this.easeInOutCubic(Math.max(0, Math.min(1, t01)));
    lerpCameraState(this.camera, CAMERA_STATES.topView, CAMERA_STATES.midView, t * 0.35);
  }

  private updateCameraWallGeneration(sectionT01: number): void {
    const t = this.easeInOutCubic(Math.max(0, Math.min(1, sectionT01)));
    lerpCameraState(this.camera, CAMERA_STATES.midView, CAMERA_STATES.wallFocusView, t);
  }

  private forEachFurnitureMesh(fn: (mesh: THREE.Mesh, material: THREE.MeshStandardMaterial) => void): void {
    this.buildingGroup.children.forEach(childGroup => {
      if (!(childGroup instanceof THREE.Group)) return;

      childGroup.children.forEach((child) => {
        if (!(child instanceof THREE.Mesh)) return;

        if (!child.userData.furnitureId) return;

        const material = child.material as THREE.MeshStandardMaterial;
        fn(child, material);
      });
    });
  }

  private fadeFurnitureTo(alpha01: number): void {
    const a = Math.max(0, Math.min(1, alpha01));

    this.forEachFurnitureMesh((mesh, material) => {
      mesh.visible = a > 0.001;
      material.transparent = true;
      material.opacity = a;
      mesh.scale.y = 1;
    });
  }

  private hideAllFurniture(): void {
    this.fadeFurnitureTo(0);
  }

  private showAllFurniture(): void {
    this.fadeFurnitureTo(1);
  }

  private updateBuildingFurnitureOnly(t01: number): void {
    const t = this.easeOutCubic(Math.max(0, Math.min(1, t01)));

    this.forEachFurnitureMesh((child, material) => {
      child.visible = true;

      const order = (child.userData.furnitureOrder as number | undefined) ?? 0;
      const delay = Math.min(0.22, order * 0.03);
      const local = Math.max(0, Math.min(1, (t - delay) / (1 - delay)));
      const eased = this.easeOutCubic(local);

      child.scale.y = 0.01 + eased * 0.99;
      material.transparent = true;
      material.opacity = eased;
    });
  }

  private updateCameraFurnitureFocus(sectionT01: number): void {
    const t = this.easeInOutCubic(Math.max(0, Math.min(1, sectionT01)));

    const dollyT = Math.max(0, Math.min(1, (t - 0.35) / 0.65));
    const eased = this.easeInOutCubic(dollyT);

    lerpCameraState(this.camera, CAMERA_STATES.wallFocusView, CAMERA_STATES.furnitureDollyView, eased);
  }

  private updateCameraSdk(sectionT01: number): void {
    const t = this.easeInOutCubic(Math.max(0, Math.min(1, sectionT01)));

    lerpCameraState(this.camera, CAMERA_STATES.furnitureDollyView, CAMERA_STATES.sdkFollowPathTop, t);
  }

  private updateCameraPricing(t01: number): void {
    const t = this.easeInOutCubic(Math.max(0, Math.min(1, t01)));

    lerpCameraState(this.camera, CAMERA_STATES.sdkFollowPathTop, CAMERA_STATES.wallFocusView, t);
  }

  private createSdkOverlay(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'sdk-overlay';

    const color = new THREE.Color(0.78, 0.87, 1.0);
    const pinBlue = new THREE.Color('#1e5a8a');

    const buildRoundedRightAngle = (params: {
      start: THREE.Vector3;
      corner: THREE.Vector3;
      end: THREE.Vector3;
      cornerPoints?: number;
      radius?: number;
      step?: number;
    }): THREE.Vector3[] => {
      const cornerPoints = Math.max(3, Math.min(24, params.cornerPoints ?? 8));
      const radius = Math.max(0.05, params.radius ?? 0.8);
      const step = Math.max(0.2, params.step ?? 0.8);

      const y = params.corner.y;

      const to2 = (v: THREE.Vector3) => new THREE.Vector2(v.x, v.z);
      const s2 = to2(params.start);
      const c2 = to2(params.corner);
      const e2 = to2(params.end);

      const dirIn = new THREE.Vector2().subVectors(c2, s2).normalize();
      const dirOut = new THREE.Vector2().subVectors(e2, c2).normalize();

      const dot = THREE.MathUtils.clamp(dirIn.dot(dirOut), -1, 1);
      if (Math.abs(dot) > 1e-3) {
        return [params.start.clone(), params.corner.clone(), params.end.clone()];
      }

      const tIn2 = new THREE.Vector2().addVectors(c2, dirIn.clone().multiplyScalar(-radius));
      const tOut2 = new THREE.Vector2().addVectors(c2, dirOut.clone().multiplyScalar(radius));

      const center2 = new THREE.Vector2().addVectors(tIn2, dirOut.clone().multiplyScalar(radius));

      const angle0 = Math.atan2(tIn2.y - center2.y, tIn2.x - center2.x);
      const angle1 = Math.atan2(tOut2.y - center2.y, tOut2.x - center2.x);

      let delta = angle1 - angle0;
      while (delta <= -Math.PI) delta += Math.PI * 2;
      while (delta > Math.PI) delta -= Math.PI * 2;

      const points: THREE.Vector3[] = [];

      const startToTin = new THREE.Vector2().subVectors(tIn2, s2);
      const dist1 = startToTin.length();
      const n1 = Math.max(1, Math.ceil(dist1 / step));
      for (let i = 0; i <= n1; i++) {
        const a = i / n1;
        const p2 = new THREE.Vector2().lerpVectors(s2, tIn2, a);
        points.push(new THREE.Vector3(p2.x, y, p2.y));
      }

      for (let i = 1; i <= cornerPoints; i++) {
        const a = i / cornerPoints;
        const ang = angle0 + delta * a;
        const p2 = new THREE.Vector2(center2.x + Math.cos(ang) * radius, center2.y + Math.sin(ang) * radius);
        points.push(new THREE.Vector3(p2.x, y, p2.y));
      }

      const toutToEnd = new THREE.Vector2().subVectors(e2, tOut2);
      const dist2 = toutToEnd.length();
      const n2 = Math.max(1, Math.ceil(dist2 / step));
      for (let i = 1; i <= n2; i++) {
        const a = i / n2;
        const p2 = new THREE.Vector2().lerpVectors(tOut2, e2, a);
        points.push(new THREE.Vector3(p2.x, y, p2.y));
      }

      const dedup: THREE.Vector3[] = [];
      for (const p of points) {
        const last = dedup[dedup.length - 1];
        if (!last || last.distanceToSquared(p) > 1e-8) dedup.push(p);
      }

      return dedup;
    };

    const k = 0.02;

    const anchors = [
      new THREE.Vector3(6.2, k, -3.8),
      new THREE.Vector3(4.4, k, -1.6),
      new THREE.Vector3(5.8, k, 0.6),
      new THREE.Vector3(3.2, k, 2.4),
      new THREE.Vector3(6.0, k, 4.0),
      new THREE.Vector3(2.6, k, 4.6),
      new THREE.Vector3(-0.8, k, 3.4),
      new THREE.Vector3(-3.4, k, 4.2),
      new THREE.Vector3(-6.2, k, 3.9),
    ];

    const pathPoints: THREE.Vector3[] = [];
    for (let i = 0; i < anchors.length - 1; i++) {
      const a = anchors[i];
      const b = anchors[i + 1];

      const pts = buildRoundedRightAngle({
        start: a,
        corner: new THREE.Vector3(a.x, k, b.z),
        end: b,
        cornerPoints: 8,
        radius: 0.7,
        step: 0.7,
      });

      for (let j = 0; j < pts.length; j++) {
        const p = pts[j];
        const last = pathPoints[pathPoints.length - 1];
        if (!last || last.distanceToSquared(p) > 1e-8) pathPoints.push(p);
      }
    }

    const geom = new THREE.BufferGeometry().setFromPoints(pathPoints);
    const mat = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: 0,
    });

    const line = new THREE.Line(geom, mat);
    line.name = 'sdk-path';
    line.userData.totalPoints = pathPoints.length;
    (line.geometry as THREE.BufferGeometry).setDrawRange(0, 0);
    group.add(line);

    const dotPositions: THREE.Vector3[] = [];
    for (let i = 0; i < pathPoints.length; i += 2) {
      dotPositions.push(pathPoints[i].clone());
    }
    dotPositions.forEach((p, idx) => {
      const dot = new THREE.Mesh(
        new THREE.CircleGeometry(0.06, 18),
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0,
          depthWrite: false,
        })
      );
      dot.name = `sdk-path-dot-${idx}`;
      dot.position.copy(p);
      dot.rotation.x = -Math.PI / 2;
      dot.renderOrder = 9;
      group.add(dot);
    });

    const markerPositions = [
      new THREE.Vector3(4.8, 0.02, -1.2),
      new THREE.Vector3(2.2, 0.02, 3.2),
      new THREE.Vector3(-2.4, 0.02, -3.0),
      new THREE.Vector3(-5.2, 0.02, 2.4),
    ];

    markerPositions.forEach((p, idx) => {
      const marker = new THREE.Group();
      marker.name = `sdk-loc-pin-${idx}`;
      marker.position.copy(p);
      marker.userData.revealIndex = idx;

      const ringGeom = new THREE.RingGeometry(0.18, 0.26, 48);
      const ringMat = new THREE.MeshBasicMaterial({
        color: pinBlue,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(ringGeom, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.renderOrder = 10;
      marker.add(ring);

      const pinShape = new THREE.Shape();
      pinShape.moveTo(0, 0.28);
      pinShape.bezierCurveTo(0.17, 0.28, 0.28, 0.15, 0.28, 0.02);
      pinShape.bezierCurveTo(0.28, -0.17, 0.13, -0.33, 0, -0.52);
      pinShape.bezierCurveTo(-0.13, -0.33, -0.28, -0.17, -0.28, 0.02);
      pinShape.bezierCurveTo(-0.28, 0.15, -0.17, 0.28, 0, 0.28);

      const pinGeom = new THREE.ExtrudeGeometry(pinShape, {
        depth: 0.08,
        bevelEnabled: true,
        bevelThickness: 0.018,
        bevelSize: 0.015,
        bevelSegments: 2,
        curveSegments: 24,
      });
      pinGeom.center();

      const pinMat = new THREE.MeshBasicMaterial({
        color: pinBlue,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      const pinMesh = new THREE.Mesh(pinGeom, pinMat);
      pinMesh.position.y = 0.28;
      pinMesh.renderOrder = 11;
      marker.add(pinMesh);

      const pinInnerGeom = new THREE.CircleGeometry(0.09, 28);
      const pinInnerMat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      const pinInner = new THREE.Mesh(pinInnerGeom, pinInnerMat);
      pinInner.position.set(0, 0.33, 0.045);
      pinInner.renderOrder = 12;
      marker.add(pinInner);

      marker.scale.setScalar(1);
      group.add(marker);
    });

    return group;
  }

  private updateSdkOverlay(sectionT01: number): void {
    const t = this.easeInOutCubic(Math.max(0, Math.min(1, sectionT01)));

    const line = this.sdkOverlayGroup.getObjectByName('sdk-path') as THREE.Line | null;
    if (line) {
      const geom = line.geometry as THREE.BufferGeometry;
      const total = (line.userData.totalPoints as number) ?? geom.getAttribute('position').count;

      const drawT = this.easeOutCubic(Math.min(1, t / 0.7));
      const drawCount = Math.max(2, Math.floor(total * drawT));
      geom.setDrawRange(0, Math.min(drawCount, total));

      const mat = line.material as THREE.LineBasicMaterial;
      mat.opacity = this.lerp(mat.opacity, 0.55, 0.06) * t;

      this.sdkOverlayGroup.children.forEach((obj) => {
        if (!(obj instanceof THREE.Mesh)) return;
        if (!obj.name.startsWith('sdk-path-dot-')) return;

        const idx = Number(obj.name.replace('sdk-path-dot-', ''));
        const dotIndexInPath = idx * 2;

        const local = (drawCount - dotIndexInPath) / 4;
        const reveal = Math.max(0, Math.min(1, local));
        const eased = this.easeOutCubic(reveal);

        const m = obj.material as THREE.MeshBasicMaterial;
        m.opacity = this.lerp(m.opacity, 0.38, 0.1) * eased;
      });
    }

    this.sdkOverlayGroup.children.forEach((obj) => {
      if (!(obj instanceof THREE.Group)) return;
      if (!obj.name.startsWith('sdk-loc-pin-')) return;

      const idx = (obj.userData.revealIndex as number | undefined) ?? 0;

      const baseStart = 0.54;
      const step = 0.16;
      const local = (t - (baseStart + idx * step)) / 0.22;
      const reveal = Math.max(0, Math.min(1, local));
      const eased = this.easeOutCubic(reveal);

      let scale = 0;
      if (reveal > 0) {
        if (reveal < 0.72) {
          const p = reveal / 0.72;
          scale = 1.1 * this.easeOutCubic(p);
        } else {
          const p = (reveal - 0.72) / 0.28;
          scale = this.lerp(1.1, 1.0, this.easeInOutCubic(p));
        }
      }
      obj.scale.setScalar(scale);

      obj.children.forEach((c) => {
        const mesh = c as THREE.Mesh;
        const m = mesh.material as THREE.MeshBasicMaterial;

        const isRing = mesh.geometry instanceof THREE.RingGeometry;
        const target = isRing ? 0.52 : 0.95;
        m.opacity = this.lerp(m.opacity, target, 0.14) * eased;
      });
    });
  }

  private getStageProgress(stage: 'assembling' | 'growing'): number {
    const range = this.config.stageRange[stage];
    if (this.progress <= range.start) return 0;
    if (this.progress >= range.end) return 1;
    return (this.progress - range.start) / (range.end - range.start);
  }

  private updateBlueprintAnimation(assembleProgress: number): void {
    const easedProgress = this.easeInOutCubic(Math.min(assembleProgress * 1.5, 1));

    this.blueprintGroup.children.forEach(group => {
      if (group instanceof THREE.Group) {
        group.children.forEach((lineObj) => {
          const line = lineObj as THREE.Line;
          const userData = line.userData;

          if (!userData.originalPositions || !userData.scatteredPositions) return;

          const posAttr = line.geometry.getAttribute('position');
          const original = userData.originalPositions;
          const scattered = userData.scatteredPositions;

          const count = Math.min(posAttr.count, original.length);
          for (let i = 0; i < count; i++) {
            const orig = original[i];
            const scat = scattered[i];

            posAttr.setXYZ(i,
              scat.x + (orig.x - scat.x) * easedProgress,
              scat.y + (orig.y - scat.y) * easedProgress,
              scat.z + (orig.z - scat.z) * easedProgress
            );
          }
          posAttr.needsUpdate = true;

          line.rotation.y = (line.userData.scatterOffset?.rotY || 0) * (1 - easedProgress);
          line.scale.setScalar(0.6 + 0.4 * (1 - easedProgress) + easedProgress);

          const material = line.material as THREE.LineBasicMaterial;
          const isWall = userData.type === 'wall';
          const isDoor = userData.type === 'door';
          const isWindow = userData.type === 'window';
          const isFurniture = userData.type === 'furniture' || userData.furnitureId;

          if (assembleProgress < 0.5) {
            const targetOpacity = isWall ? 0.9 : isDoor ? 0.85 : isWindow ? 0.7 : 0.75;
            material.opacity = targetOpacity;
          } else if (assembleProgress < 0.8) {
            if (isWall || isDoor || isWindow) {
              material.opacity = 0;
            } else {
              material.opacity = 0.5;
            }
          } else {
            if (isFurniture) {
              material.opacity = 0;
            }
          }
        });
      }
    });
  }

  private updateBuildingAnimation(growProgress: number): void {
    if (growProgress <= 0) {
      this.buildingGroup.visible = false;
      return;
    }

    this.buildingGroup.visible = true;

    this.buildingGroup.children.forEach(childGroup => {
      if (childGroup instanceof THREE.Group) {
        childGroup.children.forEach((child) => {
          if (child instanceof THREE.Mesh) {
            const material = child.material as THREE.MeshStandardMaterial;
            const isFurniture = !!child.userData.furnitureId;

            if (child.userData.isExterior !== undefined || child.parent?.name === '3d-floor' || child.userData.isCorner) {
              if (growProgress < 0.8) {
                const wallProgress = this.easeOutQuad(growProgress / 0.8);
                child.scale.y = 0.01 + wallProgress * 0.99;
                material.opacity = wallProgress;
              } else {
                child.scale.y = 1;
                material.opacity = 1;
              }
            }

            if (isFurniture) {
              child.visible = false;
              child.scale.y = 0.01;
              material.opacity = 0;
            } else {
              child.visible = true;
            }
          }
        });
      }
    });
  }

  private updateCameraAnimation(assembleProgress: number, growProgress: number): void {
    if (assembleProgress < 1) {
      const cameraProgress = this.easeInOutCubic(assembleProgress);
      const from = CAMERA_STATES.topView;
      const to = CAMERA_STATES.midView;
      lerpCameraState(this.camera, from, to, cameraProgress);
    }

    if (growProgress > 0) {
      const cameraProgress = this.easeInOutCubic(growProgress);
      const from = CAMERA_STATES.midView;
      const to = CAMERA_STATES.threeDView;
      lerpCameraState(this.camera, from, to, cameraProgress);
    }
  }

  private updateLightAnimation(assembleProgress: number, growProgress: number): void {
    const lights = this.scene.userData.lights;
    if (!lights) return;

    const ambientTarget = 0.15 + assembleProgress * 0.1;
    const mainTarget = 0.4 + growProgress * 0.6;
    const topTarget = 0.3 + growProgress * 0.5;

    lights.ambient.intensity = ambientTarget;
    lights.main.intensity = mainTarget;
    lights.top.intensity = topTarget;
  }

  private lerp(start: number, end: number, t: number): number {
    return start + (end - start) * t;
  }

  private easeInOutCubic(t: number): number {
    t = Math.max(0, Math.min(1, t));
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  private easeOutQuad(t: number): number {
    return 1 - (1 - t) * (1 - t);
  }

  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  public getStage(): AnimationStage {
    return this.stage;
  }

  public getProgress(): number {
    return this.progress;
  }

  public reset(): void {
    this.progress = 0;
    this.setInitialState();
  }

  public dispose(): void {
    // 清理资源
  }
}

export class ScrollController {
  private progress: number = 0;
  private container: HTMLElement | null = null;
  private onProgressChange: (progress: number) => void;
  private isAnimating: boolean = false;
  private targetProgress: number = 0;
  private animationId: number | null = null;
  private lastTouchY: number = 0;

  private gestureLocked = false;
  private gestureUnlockTimer: number | null = null;
  private gestureCooldownMs = 260;

  private minGestureDelta = 0.00035;

  private snapPoints: number[] = [
    this.holdEndSnap(0.0, 0.05, 0.78),
    this.holdEndSnap(0.05, 0.18, 0.82),
    this.holdEndSnap(0.18, 0.60, 0.80),
    this.holdEndSnap(0.60, 0.82, 0.84),
    this.holdEndSnap(0.82, 0.96, 0.92),
    this.holdEndSnap(0.96, 1.0, 0.95),
  ];

  private wheelFactorBase = 0.00011 / 0.85;
  private touchFactorBase = 0.0024 / 0.85;

  private smoothFactor = 0.0375;

  private holdEndSnap(start: number, end: number, holdEnd: number): number {
    const lp = Math.max(0, Math.min(1, holdEnd));
    return start + (end - start) * lp;
  }

  constructor(
    container: HTMLElement,
    onProgressChange: (progress: number) => void
  ) {
    this.container = container;
    this.onProgressChange = onProgressChange;
    this.bindEvents();
  }

  private bindEvents(): void {
    if (!this.container) return;
    this.container.addEventListener('wheel', this.handleWheel, { passive: false });
    this.container.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    this.container.addEventListener('touchmove', this.handleTouchMove, { passive: false });
  }

  private applyDelta(rawDeltaProgress: number): void {
    const abs = Math.abs(rawDeltaProgress);
    if (abs < this.minGestureDelta) return;

    const dir: 1 | -1 = rawDeltaProgress >= 0 ? 1 : -1;

    if (this.gestureLocked) return;
    this.lockGesture();

    const currentIdx = this.getNearestSnapIndex(this.targetProgress);
    const nextIdx = Math.max(0, Math.min(this.snapPoints.length - 1, currentIdx + dir));

    this.targetProgress = this.snapPoints[nextIdx];
    this.startSmoothAnimation();
  }

  private lockGesture(): void {
    this.gestureLocked = true;

    if (this.gestureUnlockTimer !== null) {
      window.clearTimeout(this.gestureUnlockTimer);
    }

    this.gestureUnlockTimer = window.setTimeout(() => {
      this.gestureLocked = false;
      this.gestureUnlockTimer = null;
    }, this.gestureCooldownMs);
  }

  private getNearestSnapIndex(p: number): number {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < this.snapPoints.length; i++) {
      const d = Math.abs(this.snapPoints[i] - p);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  private handleWheel = (e: WheelEvent): void => {
    e.preventDefault();

    const isTrackpad = Math.abs(e.deltaY) < 60;
    const factor = this.wheelFactorBase * (isTrackpad ? 0.75 : 1);

    const delta = e.deltaY * factor;
    this.applyDelta(delta);
  };

  private handleTouchStart = (e: TouchEvent): void => {
    if (e.touches.length === 1) {
      this.lastTouchY = e.touches[0].clientY;
    }
  };

  private handleTouchMove = (e: TouchEvent): void => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const deltaY = e.touches[0].clientY - this.lastTouchY;
      this.lastTouchY = e.touches[0].clientY;

      const delta = -deltaY * this.touchFactorBase;
      this.applyDelta(delta);
    }
  };

  private startSmoothAnimation(): void {
    if (this.isAnimating) return;
    this.isAnimating = true;

    const animate = () => {
      const diff = this.targetProgress - this.progress;

      if (Math.abs(diff) > 0.00008) {
        this.progress += diff * this.smoothFactor;
        this.onProgressChange(this.progress);
        this.animationId = requestAnimationFrame(animate);
        return;
      }

      this.progress = this.targetProgress;
      this.onProgressChange(this.progress);
      this.isAnimating = false;
    };

    this.animationId = requestAnimationFrame(animate);
  }

  public setProgress(progress: number): void {
    this.progress = Math.max(0, Math.min(1, progress));
    this.targetProgress = this.progress;
    this.onProgressChange(this.progress);
  }

  public getProgress(): number {
    return this.progress;
  }

  public dispose(): void {
    if (this.container) {
      this.container.removeEventListener('wheel', this.handleWheel);
      this.container.removeEventListener('touchstart', this.handleTouchStart);
      this.container.removeEventListener('touchmove', this.handleTouchMove);
    }
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.gestureUnlockTimer !== null) {
      window.clearTimeout(this.gestureUnlockTimer);
      this.gestureUnlockTimer = null;
    }
  }
}

export type { THREE };
